// /stacks/traffic/dev/frontend/src/features/stats/PlateFilter.jsx
import React, { useRef, useState } from "react";

/**
 * Expected props (as used by Stats.jsx):
 *  - plate: string | undefined            // currently selected plate (optional, for display only)
 *  - plateText: string                    // current text in the box
 *  - setPlateText: (s: string) => void    // update text as user types
 *  - plateMatches: string[]               // suggestions (partial results) to render
 *  - onSearch: () => void                 // called on Enter / Search
 *  - onClear: () => void                  // clear input + results
 *  - onPick: (plate: string) => void      // pick a suggestion
 */
export default function PlateFilter({
  plate,
  plateText,
  setPlateText,
  plateMatches = [],
  onSearch,
  onClear,
  onPick,
}) {
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef(null);

  const handleChange = (e) => {
    // Keep text raw; exact alias is handled upstream when text starts with "="
    setPlateText(String(e.target.value ?? ""));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !isComposing) {
      e.preventDefault();
      onSearch?.();
    }
  };

  const handlePick = (p) => {
    // Use mousedown to avoid blur swallowing the click on mobile
    onPick?.(p);
  };

  const hasText = (plateText ?? "").length > 0;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        inputMode="text"
        autoCorrect="off"
        autoCapitalize="none"
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
        placeholder="Search plate — use =ALIAS for exact"
        aria-label="Search plate"
        value={plateText ?? ""}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        className="input"
        style={{
          height: 32,
          background: "#0d1420",
          border: "1px solid #1f2a39",
          color: "#dbe7ff",
          borderRadius: 6,
          padding: "0 10px",
          width: 260,
          cursor: "text",
          WebkitUserSelect: "text",
          WebkitTouchCallout: "default",
          touchAction: "manipulation",
        }}
      />

      <button
        type="button"
        className="btn btn--sm"
        onClick={() => onSearch?.()}
        title="Search"
        style={{
          height: 32,
          padding: "0 10px",
          borderRadius: 6,
          background: "#14304f",
          border: "1px solid #2a4d77",
          color: "#dbe7ff",
        }}
      >
        Search
      </button>

      <button
        type="button"
        className="btn btn--sm"
        onClick={() => {
          onClear?.();
          inputRef.current?.focus?.();
        }}
        title="Clear"
        disabled={!hasText && !plate}
        style={{
          height: 32,
          padding: "0 10px",
          borderRadius: 6,
          background: "#1b2230",
          border: "1px solid #2a2f45",
          color: "#cbd5e1",
          opacity: !hasText && !plate ? 0.6 : 1,
        }}
      >
        Clear
      </button>

      {/* Suggestions dropdown (partial matches). Uses mousedown to register picks on mobile without blur issues. */}
      {Array.isArray(plateMatches) && plateMatches.length > 0 && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: 40,
            left: 0,
            width: 260,
            maxHeight: 240,
            overflowY: "auto",
            background: "#0b1120",
            border: "1px solid #1f2a39",
            borderRadius: 6,
            zIndex: 50,
            boxShadow: "0 6px 18px rgba(0,0,0,.35)",
          }}
          // Prevent outer containers with click-away from swallowing taps before we handle them
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {plateMatches.map((p) => (
            <div
              key={p}
              role="option"
              onMouseDown={() => handlePick(p)}
              onTouchStart={() => handlePick(p)}
              style={{
                padding: "8px 10px",
                color: "#e5efff",
                cursor: "pointer",
                borderBottom: "1px solid #132033",
              }}
            >
              {p}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
